import { INPUT_CLASS, LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import { cn } from '@/lib/utils';
import { getItLabImages } from '@/lib/zybooks-api';
import type { ReactNodeViewProps } from '@tiptap/react';
import { Check, ChevronsUpDown, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { syncGoalVmImageTests } from './zyitlab-extension';
import { InstanceTypeSelect } from './zyitlab-shared';

let itLabImageNamesPromise: Promise<string[]> | null = null;

function loadItLabImageNames(): Promise<string[]> {
    if (!itLabImageNamesPromise) {
        itLabImageNamesPromise = getItLabImages()
            .then(response => {
                const env = import.meta.env.MODE === 'production' ? 'production' : 'development';
                return response.itlab_images[env].map(image => image.image_name);
            })
            .catch(() => []);
    }
    return itLabImageNamesPromise;
}

interface VmImageNameSelectProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

function VmImageNameSelect({ value, onChange, disabled }: VmImageNameSelectProps) {
    const [open, setOpen] = useState(false);
    const [imageNames, setImageNames] = useState<string[]>([]);

    useEffect(() => {
        loadItLabImageNames().then(setImageNames);
    }, []);

    const options = useMemo(() => {
        if (value && !imageNames.includes(value)) {
            return [value, ...imageNames];
        }
        return imageNames;
    }, [value, imageNames]);

    return (
        <div contentEditable={false}>
            <label
                className={LABEL_CLASS}
                contentEditable={false}
            >
                VM image
            </label>
            <Popover
                open={open}
                onOpenChange={setOpen}
            >
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            INPUT_CLASS,
                            'h-auto min-h-0 justify-between font-normal shadow-none hover:bg-white dark:hover:bg-gray-900',
                        )}
                        contentEditable={false}
                    >
                        <span className="truncate">{value || 'Select VM image…'}</span>
                        <ChevronsUpDown
                            className="ml-2 size-4 shrink-0 opacity-50"
                            aria-hidden
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                >
                    <Command>
                        <CommandInput placeholder="Search VM images…" />
                        <CommandList>
                            <CommandEmpty>No VM image found.</CommandEmpty>
                            <CommandGroup>
                                {options.map(name => (
                                    <CommandItem
                                        key={name}
                                        value={name}
                                        onSelect={() => {
                                            onChange(name);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn('mr-2 size-4', value === name ? 'opacity-100' : 'opacity-0')}
                                            aria-hidden
                                        />
                                        {name}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}

export function ZyITLabVmImageNodeView({ node, updateAttributes, editor, getPos, decorations }: ReactNodeViewProps) {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'zyITLabVmImage',
    });
    const pos = getPos();
    const canDelete = totalNodes > 1;

    const handleDelete = () => {
        if (typeof pos !== 'number' || !canDelete) {
            return;
        }
        const zyITLabPos = editor.state.doc.resolve(pos).before(-1);
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run();
        const refreshed = editor.state.doc.nodeAt(zyITLabPos);
        if (refreshed) {
            syncGoalVmImageTests(editor, refreshed, zyITLabPos);
        }
    };

    return (
        <ZyNodeViewWrapper decorations={decorations}>
            <div
                className="mb-3 rounded border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-950"
                contentEditable={false}
            >
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">VM {nodeIndex + 1}</span>
                    {canDelete && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Remove VM image"
                            disabled={isEditorReadonly}
                            onClick={handleDelete}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
                <div className="flex flex-col gap-3">
                    <div>
                        <label
                            className={LABEL_CLASS}
                            contentEditable={false}
                        >
                            Title
                        </label>
                        <input
                            className={INPUT_CLASS}
                            value={node.attrs.title || ''}
                            onChange={e => updateAttributes({ title: e.target.value })}
                            disabled={isEditorReadonly}
                        />
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <VmImageNameSelect
                            value={node.attrs.name || ''}
                            onChange={name => updateAttributes({ name })}
                            disabled={isEditorReadonly}
                        />
                        <InstanceTypeSelect
                            value={node.attrs.instanceType || 'small'}
                            onChange={instanceType => updateAttributes({ instanceType })}
                            disabled={isEditorReadonly}
                        />
                    </div>
                </div>
                <div className="mt-3">
                    <label
                        className={LABEL_CLASS}
                        contentEditable={false}
                    >
                        Startup commands
                    </label>
                    <textarea
                        className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        rows={3}
                        placeholder="One command per line"
                        value={node.attrs.startupCommands || ''}
                        onChange={e => updateAttributes({ startupCommands: e.target.value })}
                        disabled={isEditorReadonly}
                    />
                </div>
            </div>
        </ZyNodeViewWrapper>
    );
}
